import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ShoppingBag,
  Globe,
  Phone,
  MessageSquare,
  Image,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";

export interface AvailableNumber {
  number: string;
  location: string;
  type: "local" | "toll-free";
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  addressRequired: boolean;
  monthlyPrice: number;
}

// API response type
interface TwilioAvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string;
  region: string;
  isoCountry: string;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  type: "local" | "toll-free";
  monthlyPrice: number;
  addressRequirements: string;
}

interface BuyNumberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchase: (number: AvailableNumber) => void;
}

export function BuyNumberModal({ open, onOpenChange, onPurchase }: BuyNumberModalProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [country] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [numberType, setNumberType] = useState<"local" | "toll-free" | "all">("all");
  const [capabilities, setCapabilities] = useState({
    voice: true,
    sms: true,
    mms: false,
  });
  const [searchResults, setSearchResults] = useState<AvailableNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Search mutation
  const searchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/agency/phone-numbers/search", {
        country,
        areaCode: areaCode || undefined,
        type: numberType === "all" ? undefined : numberType,
        smsEnabled: capabilities.sms,
        voiceEnabled: capabilities.voice,
        mmsEnabled: capabilities.mms || undefined,
        limit: 20,
      });
      return res.json() as Promise<TwilioAvailableNumber[]>;
    },
    onSuccess: (data) => {
      setHasSearched(true);
      // Convert API response to AvailableNumber format
      const results: AvailableNumber[] = data.map((n) => ({
        number: n.phoneNumber,
        location: n.locality ? `${n.locality}, ${n.region}` : (n.type === "toll-free" ? "Toll Free" : n.region || ""),
        type: n.type,
        capabilities: n.capabilities,
        addressRequired: n.addressRequirements !== "none",
        monthlyPrice: n.monthlyPrice,
      }));
      setSearchResults(results);
    },
  });

  const handleSearch = () => {
    searchMutation.mutate();
  };

  const handlePurchase = () => {
    const number = searchResults.find(n => n.number === selectedNumber);
    if (number) {
      onPurchase(number);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchResults([]);
    setSelectedNumber(null);
    setAreaCode("");
    setNumberType("all");
    setCapabilities({ voice: true, sms: true, mms: false });
    setHasSearched(false);
    onOpenChange(false);
  };

  const selectedCount = selectedNumber ? 1 : 0;
  const isSearching = searchMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <ShoppingBag className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Buy your Number</DialogTitle>
              <DialogDescription>
                You need to complete a few easy steps to get started with a new number.{" "}
                <a href="#" className="text-primary hover:underline inline-flex items-center gap-1">
                  Learn More <ExternalLink className="h-3 w-3" />
                </a>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Country & Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Country</Label>
                <Select value={country} disabled>
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        United States
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        United States
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Collapsible open={filterOpen} onOpenChange={setFilterOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      Filter
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform",
                        filterOpen && "rotate-180"
                      )} />
                    </Button>
                  </CollapsibleTrigger>
                </Collapsible>
              </div>
            </div>

            {/* Expandable Filter Options */}
            <Collapsible open={filterOpen} onOpenChange={setFilterOpen}>
              <CollapsibleContent className="space-y-4 pt-3 border-t">
                {/* Area Code */}
                <div className="space-y-1.5">
                  <Label htmlFor="area-code">Area Code</Label>
                  <Input
                    id="area-code"
                    placeholder="e.g., 212, 415"
                    maxLength={3}
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                    className="max-w-[200px]"
                  />
                </div>

                {/* Number Type */}
                <div className="space-y-1.5">
                  <Label>Number Type</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={numberType === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNumberType("all")}
                    >
                      All
                    </Button>
                    <Button
                      variant={numberType === "local" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNumberType("local")}
                    >
                      Local
                    </Button>
                    <Button
                      variant={numberType === "toll-free" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNumberType("toll-free")}
                    >
                      Toll Free
                    </Button>
                  </div>
                </div>

                {/* Capabilities */}
                <div className="space-y-1.5">
                  <Label>Capabilities</Label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cap-voice"
                        checked={capabilities.voice}
                        onCheckedChange={(checked) =>
                          setCapabilities(prev => ({ ...prev, voice: !!checked }))
                        }
                      />
                      <Label htmlFor="cap-voice" className="text-sm font-normal cursor-pointer">
                        Voice
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cap-sms"
                        checked={capabilities.sms}
                        onCheckedChange={(checked) =>
                          setCapabilities(prev => ({ ...prev, sms: !!checked }))
                        }
                      />
                      <Label htmlFor="cap-sms" className="text-sm font-normal cursor-pointer">
                        SMS
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cap-mms"
                        checked={capabilities.mms}
                        onCheckedChange={(checked) =>
                          setCapabilities(prev => ({ ...prev, mms: !!checked }))
                        }
                      />
                      <Label htmlFor="cap-mms" className="text-sm font-normal cursor-pointer">
                        MMS
                      </Label>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          {/* Search Button */}
          <Button onClick={handleSearch} disabled={isSearching} className="w-full">
            {isSearching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {hasSearched ? "Refresh Results" : "Search Available Numbers"}
              </>
            )}
          </Button>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                {searchResults.length} number{searchResults.length !== 1 ? "s" : ""} available
              </Label>
              <RadioGroup
                value={selectedNumber || ""}
                onValueChange={setSelectedNumber}
                className="space-y-2"
              >
                {searchResults.map((number) => (
                  <label
                    key={number.number}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      selectedNumber === number.number
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted"
                    )}
                  >
                    <RadioGroupItem value={number.number} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{number.number}</span>
                        <span className="text-sm text-muted-foreground">/ {number.location}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex items-center gap-1.5">
                          {number.capabilities.voice && (
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          {number.capabilities.sms && (
                            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          {number.capabilities.mms && (
                            <Image className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </div>
                        <Badge variant="secondary" className="text-xs capitalize">
                          {number.type === "toll-free" ? "Toll Free" : "Local"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Address: {number.addressRequired ? "Required" : "None"}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">${number.monthlyPrice.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground">/mo</span>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}

          {hasSearched && searchResults.length === 0 && !isSearching && (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No numbers found matching your criteria</p>
              <p className="text-sm">Try adjusting your filters or area code</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedCount} Number selected
          </span>
          <Button onClick={handlePurchase} disabled={selectedCount === 0}>
            Proceed to Buy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
